import { DialogComponent } from "@theme/dialog";
import { isHtmlDialogElement } from "@theme/utilities";
import { MysaReviewsHost } from "@theme/mysa-reviews-host";
import {
  DEFAULT_SORT,
  absoluteProductUrl,
  fetchWidgetPage,
} from "@theme/mysa-reviews-api";
import {
  ALL_GALLERY_THUMB_LIMIT,
  fetchAllReviewsPage,
} from "@theme/mysa-reviews-arp";
import {
  bindReviewCard,
  photoAccessibleName,
  pictureUrls,
} from "@theme/mysa-reviews-card";
import { formatLocalizedCounts } from "@theme/mysa-reviews-ui";

/**
 * @typedef {import('@theme/mysa-reviews-api').JudgeMeReview} JudgeMeReview
 * @typedef {import('@theme/mysa-reviews-api').JudgeMeTranslations} JudgeMeTranslations
 */

/**
 * @typedef {{
 *   src: string,
 *   full: string,
 *   name?: string,
 *   review: JudgeMeReview,
 * }} GalleryItem
 */

/**
 * @typedef {{
 *   status?: HTMLElement,
 *   primaryList?: HTMLUListElement,
 *   sort?: HTMLSelectElement,
 *   productTab?: HTMLButtonElement,
 *   storeTab?: HTMLButtonElement,
 *   allPanel?: HTMLElement,
 *   gallery?: HTMLElement,
 *   galleryWrap?: HTMLElement,
 *   loadMoreAll?: HTMLButtonElement,
 *   cardTemplate?: HTMLTemplateElement,
 *   photoTemplate?: HTMLTemplateElement,
 *   galleryThumbTemplate?: HTMLTemplateElement,
 *   lightbox?: MysaReviewsLightbox,
 * }} ReviewsAllRefs
 */

/**
 * Shop-wide reviews page. Product tab parses Judge.me ARP HTML in memory;
 * store tab uses the same JSON widget API as the PDP with product_id=0.
 * @extends {MysaReviewsHost}
 */
class MysaReviewsAll extends MysaReviewsHost {
  #sortBy = DEFAULT_SORT;

  #allStream = "product";

  #allPage = 0;

  #productTotal = 0;

  #storeTotal = 0;

  /**
   * Product ARP has landed at least once. `#productTotal` starts at 0; writing
   * that over the SSR `[data-count]` zeros the Product tab if Store is opened
   * first (that abort never sets a real total).
   */
  #productTotalKnown = false;

  /** @type {GalleryItem[]} */
  #galleryItems = [];

  #galleryIndex = 0;

  /** @type {JudgeMeTranslations} */
  #galleryTranslations = {};

  connectedCallback() {
    super.connectedCallback();
    this.#bootstrap();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bootstrap();
  }

  disconnectedCallback() {
    this.killListReveal([this.refs.primaryList]);
    this.#closeLightbox();
    super.disconnectedCallback();
  }

  #bootstrap() {
    const select = this.refs.sort;
    this.#sortBy =
      select instanceof HTMLSelectElement
        ? select.value || DEFAULT_SORT
        : DEFAULT_SORT;
    this.#allStream = "product";
    this.#allPage = 0;
    this.#productTotalKnown = false;
    this.#productTotal = 0;
    this.#storeTotal = 0;
    this.#closeLightbox();
    this.#syncTabs();
    formatLocalizedCounts(this, this.dataset.locale || "en");
    this.#loadPage({ replace: true, page: 1 });
  }

  /**
   * @param {Event} event
   */
  onSortChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    this.#sortBy = select.value || DEFAULT_SORT;
    this.#loadPage({ replace: true, page: 1 });
  }

  /**
   * @param {Event} event
   */
  onTabClick(event) {
    const tab = event.target;
    if (!(tab instanceof HTMLButtonElement)) return;
    const stream = tab.dataset.stream === "store" ? "store" : "product";
    if (stream === this.#allStream) return;
    this.#allStream = stream;
    this.#syncTabs();
    this.#loadPage({ replace: true, page: 1 });
  }

  loadMore() {
    this.#loadPage({ replace: false });
  }

  /**
   * Gallery thumbs and See more open the photo + review dialog.
   * @param {Event} event
   */
  onGalleryClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.classList.contains("mysa-reviews-gallery__see-more")) {
      event.preventDefault();
      this.#openLightbox(ALL_GALLERY_THUMB_LIMIT);
      return;
    }
    if (
      !(target instanceof HTMLElement) ||
      target.dataset.galleryIndex == null
    ) {
      return;
    }
    event.preventDefault();
    this.#openLightbox(Number(target.dataset.galleryIndex) || 0);
  }

  onLightboxPrev() {
    this.#openLightbox(this.#galleryIndex - 1);
  }

  onLightboxNext() {
    this.#openLightbox(this.#galleryIndex + 1);
  }

  #syncTabs() {
    const productSelected = this.#allStream === "product";
    if (this.refs.productTab instanceof HTMLButtonElement) {
      this.refs.productTab.setAttribute(
        "aria-selected",
        productSelected ? "true" : "false",
      );
    }
    if (this.refs.storeTab instanceof HTMLButtonElement) {
      this.refs.storeTab.setAttribute(
        "aria-selected",
        productSelected ? "false" : "true",
      );
    }
    if (this.refs.allPanel instanceof HTMLElement) {
      this.refs.allPanel.setAttribute(
        "aria-labelledby",
        productSelected ? "MysaReviewsProductTab" : "MysaReviewsStoreTab",
      );
    }
    if (!productSelected) this.#hideGallery();
  }

  /**
   * @param {{ replace?: boolean, page?: number }} [options]
   */
  async #loadPage(options = {}) {
    if (!this.reviewsCdnEnabled()) return;
    const { token, signal } = this.beginLoad();
    const replace = options.replace === true;
    const nextPage = options.page || this.#allPage + 1;
    const total =
      this.#allStream === "store" ? this.#storeTotal : this.#productTotal;
    const perPage = Number(this.dataset.perPage) || 10;
    const totalPages = total > 0 ? Math.ceil(total / perPage) : 1;

    if (!replace && this.#allPage > 0 && nextPage > totalPages) {
      this.#syncLoadMore();
      return;
    }

    this.setBusy(replace);
    if (replace) {
      this.killListReveal([this.refs.primaryList]);
      this.setHostListPending(this.refs.primaryList, true);
    }
    if (this.refs.loadMoreAll instanceof HTMLButtonElement) {
      this.refs.loadMoreAll.disabled = true;
    }
    this.setHostStatus(this.dataset.i18nLoading || "", {
      screenReaderOnly: true,
    });

    const shopDomain = this.dataset.shopDomain || "";
    const locale = this.dataset.locale || "en";
    const perPageParam = this.dataset.perPage || "10";

    try {
      const pagePromise =
        this.#allStream === "store"
          ? this.#fetchStorePage(nextPage, signal)
          : fetchAllReviewsPage({
              signal,
              shopDomain,
              page: nextPage,
              perPage: perPageParam,
              locale,
              sortBy: this.#sortBy,
            });
      const picturesPromise =
        replace && this.#allStream === "product"
          ? fetchAllReviewsPage({
              signal,
              shopDomain,
              page: 1,
              perPage: perPageParam,
              locale,
              sortBy: "with-pictures",
            })
          : Promise.resolve(null);
      const productCountPromise =
        this.#allStream === "store" && !this.#productTotalKnown
          ? fetchAllReviewsPage({
              signal,
              shopDomain,
              page: 1,
              perPage: perPageParam,
              locale,
              sortBy: this.#sortBy,
              includeTranslations: false,
            }).catch((error) => {
              if (
                error instanceof DOMException &&
                error.name === "AbortError"
              ) {
                throw error;
              }
              this.logLoadError("Failed to load product review count", error);
              return null;
            })
          : Promise.resolve(null);

      const [payload, pictures, productCount] = await Promise.all([
        pagePromise,
        picturesPromise,
        productCountPromise,
      ]);
      if (!this.isCurrentLoad(token)) return;
      // ARP page 2+ omits number_of_*_reviews. Missing must not become 0.
      if (this.#allStream === "product") {
        this.#productTotal = payload.productTotal ?? this.#productTotal;
        this.#productTotalKnown = true;
      } else if (productCount) {
        this.#productTotal = productCount.productTotal ?? this.#productTotal;
        this.#productTotalKnown = true;
      }
      this.#storeTotal = payload.storeTotal ?? this.#storeTotal;
      this.#allPage = nextPage;
      this.#syncTabCounts();

      this.paintList(
        this.refs.primaryList,
        payload.reviews,
        payload.translations || {},
        { replace },
      );

      if (pictures) {
        this.#fillGallery(pictures.reviews, pictures.translations || {});
      }
      if (replace && this.#allStream === "store") this.#hideGallery();

      if (!this.isCurrentLoad(token)) return;
      this.#syncLoadMore();
      this.setHostStatus("");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (!this.isCurrentLoad(token)) return;
      this.logLoadError("Failed to load shop reviews", error);
      this.setHostStatus(this.dataset.i18nLoadError || "");
    } finally {
      if (!this.isCurrentLoad(token)) return;
      this.setBusy(false);
      this.setHostListPending(this.refs.primaryList, false);
      if (this.refs.loadMoreAll instanceof HTMLButtonElement) {
        this.refs.loadMoreAll.disabled = false;
      }
    }
  }

  /**
   * Leave the Product-tab count blank until a product-stream payload lands.
   * Shop-wide metafield count includes store reviews — do not use it here.
   * Store JSON has no product total — do not treat `#productTotal` (init 0) as one.
   */
  #syncTabCounts() {
    const locale = this.dataset.locale === "fr" ? "fr-CA" : "en-CA";
    const formatter = new Intl.NumberFormat(locale);
    const setTabCount = (tab, total) => {
      if (!(tab instanceof HTMLButtonElement)) return;
      const countEl = tab.querySelector("[data-count]");
      if (!(countEl instanceof HTMLElement)) return;
      countEl.dataset.count = String(total);
      countEl.textContent = formatter.format(total);
    };
    if (this.#productTotalKnown) {
      setTabCount(this.refs.productTab, this.#productTotal);
    }
    setTabCount(this.refs.storeTab, this.#storeTotal);
  }

  #syncLoadMore() {
    const button = this.refs.loadMoreAll;
    if (!(button instanceof HTMLButtonElement)) return;
    const total =
      this.#allStream === "store" ? this.#storeTotal : this.#productTotal;
    const perPage = Number(this.dataset.perPage) || 10;
    const totalPages = total > 0 ? Math.ceil(total / perPage) : 1;
    button.hidden = this.#allPage >= totalPages || total === 0;
  }

  #hideGallery() {
    this.#closeLightbox();
    this.#galleryItems = [];
    const wrap = this.refs.galleryWrap;
    const gallery = this.refs.gallery;
    if (gallery instanceof HTMLElement) gallery.replaceChildren();
    if (wrap instanceof HTMLElement) wrap.hidden = true;
  }

  /**
   * @param {JudgeMeReview[]} reviews
   * @param {JudgeMeTranslations} [translations]
   */
  #fillGallery(reviews, translations = {}) {
    const gallery = this.refs.gallery;
    const wrap = this.refs.galleryWrap;
    const thumbTemplate = this.refs.galleryThumbTemplate;
    if (
      !(gallery instanceof HTMLElement) ||
      !(wrap instanceof HTMLElement) ||
      !(thumbTemplate instanceof HTMLTemplateElement)
    ) {
      return;
    }

    /** @type {GalleryItem[]} */
    const thumbs = [];
    for (const review of reviews) {
      const pictures = Array.isArray(review.pictures_urls)
        ? review.pictures_urls
        : [];
      for (const picture of pictures) {
        const { src, full } = pictureUrls(picture);
        if (src) {
          thumbs.push({
            src,
            full: full || src,
            name: review.reviewer_name,
            review,
          });
        }
      }
    }

    this.#galleryItems = thumbs;
    this.#galleryTranslations = translations;
    gallery.replaceChildren();
    if (thumbs.length === 0) {
      wrap.hidden = true;
      return;
    }

    wrap.hidden = false;
    const thumbsToShow = thumbs.slice(0, ALL_GALLERY_THUMB_LIMIT);
    let index = 0;
    for (const thumb of thumbsToShow) {
      const fragment = thumbTemplate.content.cloneNode(true);
      const li = fragment.querySelector("li");
      const link = fragment.querySelector("a");
      const img = fragment.querySelector("img");
      if (
        !(li instanceof HTMLLIElement) ||
        !(img instanceof HTMLImageElement)
      ) {
        continue;
      }
      img.src = thumb.src;
      if (link instanceof HTMLAnchorElement) {
        if (thumb.full) link.href = thumb.full;
        link.dataset.galleryIndex = String(index);
        link.setAttribute("aria-haspopup", "dialog");
        link.setAttribute("aria-label", photoAccessibleName(this, thumb.name));
      }
      gallery.append(li);
      index += 1;
    }
    if (thumbs.length > ALL_GALLERY_THUMB_LIMIT) {
      const li = document.createElement("li");
      const more = document.createElement("button");
      more.type = "button";
      more.className = "mysa-reviews-gallery__see-more";
      more.setAttribute("aria-haspopup", "dialog");
      more.setAttribute("on:click", "mysa-reviews-all/onGalleryClick");
      more.textContent = this.dataset.i18nSeeMore || "";
      li.append(more);
      gallery.append(li);
    }
  }

  /**
   * @param {number} index
   */
  #openLightbox(index) {
    const total = this.#galleryItems.length;
    if (total === 0) return;
    const next = ((index % total) + total) % total;
    const item = this.#galleryItems[next];
    if (!item) return;

    const bound = bindReviewCard(this, item.review, this.#galleryTranslations);
    const card = bound?.querySelector(".mysa-reviews-card");
    if (card instanceof HTMLElement) {
      const photos = card.querySelector(".mysa-reviews-card__photos");
      if (photos instanceof HTMLElement) photos.hidden = true;
    }

    const lightbox = this.refs.lightbox;
    if (!(lightbox instanceof MysaReviewsLightbox)) return;
    this.#galleryIndex = next;
    const current = String(next + 1);
    const totalLabel = String(total);
    const positionTemplate = this.dataset.i18nPhotoOf || "";
    lightbox.showPhoto(item, total, card instanceof HTMLElement ? card : null, {
      heading: photoAccessibleName(this, item.name),
      imageLabel: photoAccessibleName(this, item.name),
      position: positionTemplate
        .replaceAll("__CURRENT__", current)
        .replaceAll("__TOTAL__", totalLabel),
    });
  }

  #closeLightbox() {
    const lightbox = this.refs.lightbox;
    if (!(lightbox instanceof MysaReviewsLightbox)) return;
    const dialog = lightbox.refs.dialog;
    if (isHtmlDialogElement(dialog) && dialog.open) {
      lightbox.closeDialog();
    }
  }

  /**
   * Store reviews via the widget JSON API (`product_id=0`).
   * Omit `productTotal` — this payload has none, and echoing `#productTotal`
   * (init 0) would look like a real count to `#syncTabCounts`.
   * @param {number} page
   * @param {AbortSignal} signal
   */
  async #fetchStorePage(page, signal) {
    const payload = await fetchWidgetPage({
      signal,
      shopDomain: this.dataset.shopDomain || "",
      productId: "0",
      productUrl: absoluteProductUrl(this.dataset.productUrl || ""),
      page,
      perPage: this.dataset.perPage || "10",
      locale: this.dataset.locale || "en",
      primaryLanguage: this.dataset.primaryLanguage || "en",
      sortBy: this.#sortBy,
      sendSort: true,
    });
    return {
      reviews: payload.primary_language_reviews ?? [],
      translations: payload.review_translations ?? {},
      storeTotal: payload.number_of_reviews ?? this.#storeTotal,
    };
  }
}

/**
 * Photo + review overlay for the shop-wide gallery. Extends the theme dialog
 * so Lenis / body scroll-lock stay balanced. Do not use Magnific / `.jdgm-*`.
 * @extends {DialogComponent}
 */
class MysaReviewsLightbox extends DialogComponent {
  requiredRefs = [
    "dialog",
    "image",
    "review",
    "prev",
    "next",
    "heading",
    "position",
  ];

  /** Page offset before `showDialog` locks the body. Restored if close lost it. */
  #pageScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("dialog:close", this.#onDialogClose);
    this.addEventListener("keydown", this.#onKey);
  }

  disconnectedCallback() {
    this.removeEventListener("dialog:close", this.#onDialogClose);
    this.removeEventListener("keydown", this.#onKey);
    super.disconnectedCallback();
  }

  showDialog() {
    if (this.refs.dialog?.open) return;
    this.#pageScrollY = window.scrollY;
    super.showDialog();
  }

  #onDialogClose = () => {
    this.#restorePageScrollIfLost(this.#pageScrollY);
  };

  /**
   * DialogComponent already restores from `body.top`. Only run if that restore
   * was lost (focus return / Lenis). Never scrollTo(0) — that clobbers a good
   * restore when the captured offset is 0.
   * @param {number} y
   */
  #restorePageScrollIfLost(y) {
    if (y <= 0) return;
    const apply = () => {
      if (window.scrollY > 0) return;
      const html = document.documentElement;
      const previous = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      window.scrollTo(0, y);
      window.__mysaLenis?.scrollTo(y, { immediate: true, force: true });
      html.style.scrollBehavior = previous;
    };
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKey = (event) => {
    if (!this.refs.dialog?.open) return;
    const host = this.closest("mysa-reviews-all");
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (host instanceof MysaReviewsAll) host.onLightboxPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      if (host instanceof MysaReviewsAll) host.onLightboxNext();
    }
  };

  /**
   * @param {GalleryItem} item
   * @param {number} total
   * @param {HTMLElement | null} card
   * @param {{ heading: string, imageLabel: string, position: string }} copy
   */
  showPhoto(item, total, card, copy) {
    const image = this.refs.image;
    if (image instanceof HTMLImageElement) {
      image.src = item.full || item.src;
      image.alt = copy.imageLabel;
    }
    if (this.refs.heading instanceof HTMLElement) {
      this.refs.heading.textContent = copy.heading;
    }
    if (this.refs.position instanceof HTMLElement) {
      this.refs.position.textContent = copy.position;
    }
    const many = total > 1;
    if (this.refs.prev instanceof HTMLButtonElement) {
      this.refs.prev.hidden = !many;
    }
    if (this.refs.next instanceof HTMLButtonElement) {
      this.refs.next.hidden = !many;
    }
    if (this.refs.review instanceof HTMLElement) {
      this.refs.review.replaceChildren();
      if (card) this.refs.review.append(card);
    }
    if (!this.refs.dialog.open) this.showDialog();
  }
}

if (!customElements.get("mysa-reviews-lightbox")) {
  customElements.define("mysa-reviews-lightbox", MysaReviewsLightbox);
}

if (!customElements.get("mysa-reviews-all")) {
  customElements.define("mysa-reviews-all", MysaReviewsAll);
}
